import { getEnabledPlanes } from '../../../shared/utils/products';

/**
 * Converts the server's `subscription` document (as returned by GET /subscriptions/{email})
 * into the modal's `checkedProducts` editing state:
 *
 *   { [productType]: { checked, regions: string[], control: string[], data: string[] } }
 *
 * Region values are the `city-<name>` leaf keys the region TreeSelects operate on (see
 * `useRegionTreeItems`), deduped because the API returns one entry per region id and
 * several region ids can share a city.
 *
 * `regions` is the union of both planes and is the source of truth for single-plane
 * products (see `buildSubscriptionPayload`). For single-plane products `control` mirrors
 * that union, matching what the pre-existing hydration did.
 *
 * Extracted from StatusPage so it can also produce the *baseline* snapshot that
 * `serializeSubscriptionState` diffs against for the modal's pristine/dirty state --
 * a pure function of server data is the only thing both the editable state and its
 * baseline can be derived from consistently.
 */
export const hydrateCheckedProducts = (subscriptionsData, productsById = {}) => {
    const subscriptions = subscriptionsData?.subscription;
    if (!subscriptions || Object.keys(subscriptions).length === 0) return {};

    const checkedProducts = {};

    Object.entries(subscriptions).forEach(([productName, productData]) => {
        const controlRegions = productData?.control?.regions || [];
        const dataRegions = productData?.data?.regions || [];
        const allRegions = [...controlRegions, ...dataRegions];

        const toCityKeys = (regions) => {
            const keys = [];
            regions.forEach((region) => {
                const key = `city-${region.city}`;
                if (!keys.includes(key)) keys.push(key);
            });
            return keys;
        };

        const unionItems = toCityKeys(allRegions);
        const product = productsById[productName];
        const multiPlane = getEnabledPlanes(product).length >= 2;

        checkedProducts[productName] = {
            checked: true,
            regions: unionItems,
            data: toCityKeys(dataRegions),
            control: multiPlane ? toCityKeys(controlRegions) : [...unionItems],
        };
    });

    return checkedProducts;
};

export default hydrateCheckedProducts;
